<main class="network-migration-page" data-cy="network-migration-page">
  <section class="pt-5">
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
      <div class="space-y-1">
        <h1 class="text-[1.375rem] leading-tight font-medium text-slate-900">
          BLE Key Transfer
        </h1>
        <p class="text-xs text-slate-500">
          Orchestrate and monitor ble transfer across all buildings.
        </p>
      </div>
      <button
        mat-flat-button
        type="button"
        color="primary"
        class="self-start text-base"
        data-cy="network-migration-start-button"
        [disabled]="startingMigration()"
        (click)="startNewMigration()">
        <span class="icon icon-play-outline align-middle mr-1" aria-hidden="true"></span>
        <span class="align-middle">Start New Transfer</span>
      </button>
    </div>
    @if (contextError()) {
      <app-notification-block [header]="'Unable to load Network Migration'" [statusClass]="StatusClass.ERROR">
        {{ contextError() }}
      </app-notification-block>
    } @else {
      <div class="mb-4">
        <span class="text-sm font-medium text-slate-800">Buildings</span>
      </div>

      <mat-card
        appearance="outlined"
        class="migration-table-card overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
        <div
          class="flex flex-col gap-4 border-b border-slate-100 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex flex-1 flex-col gap-3 xl:flex-row xl:items-center">
            <div class=" relative">
              <span class="icon search-field-icon relative"></span>
              <mat-form-field
                appearance="outline"
                subscriptSizing="dynamic"
                class="migration-filter-field migration-search-field">
                <input
                  matInput
                  type="search"
                  [value]="searchQuery()"
                  placeholder="Search buildings or IDs..."
                  data-cy="network-migration-search-input"
                  (input)="updateSearchQuery($event)"
                />
              </mat-form-field>
            </div>
          </div>
          <div class="flex items-center gap-3 text-xs font-medium text-slate-600">
            <mat-slide-toggle
              color="primary"
              [checked]="requiresAttentionOnly()"
              data-cy="network-migration-attention-toggle"
              (change)="updateRequiresAttentionFilter($event)">
              Requires Attention
            </mat-slide-toggle>
          </div>
        </div>

        @if (filteredRows().length) {
          <div class="network-migration-table-shell overflow-x-auto">
            <table
              mat-table
              [dataSource]="filteredRows()"
              class="network-migration-table min-w-[1280px]"
              data-cy="network-migration-table">
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>STATUS</th>
                <td mat-cell *matCellDef="let row">
                  <span [class]="'status-pill status-pill--' + row.statusClass">
                    <span class="status-pill-dot" aria-hidden="true"></span>
                    {{ row.statusLabel }}
                  </span>
                </td>
              </ng-container>

              <ng-container matColumnDef="buildingName">
                <th mat-header-cell *matHeaderCellDef>BUILDING NAME</th>
                <td mat-cell *matCellDef="let row">
                  <span class="text-xs font-semibold text-slate-900">{{ row.buildingName }}</span>
                </td>
              </ng-container>

              <ng-container matColumnDef="buildingId">
                <th mat-header-cell *matHeaderCellDef>BUILDING ID</th>
                <td mat-cell *matCellDef="let row">{{ row.buildingId }}</td>
              </ng-container>

              <ng-container matColumnDef="customer">
                <th mat-header-cell *matHeaderCellDef>CUSTOMER</th>
                <td mat-cell *matCellDef="let row">{{ row.customer ?? '—' }}</td>
              </ng-container>

              <ng-container matColumnDef="nodes">
                <th mat-header-cell *matHeaderCellDef>NODES</th>
                <td mat-cell *matCellDef="let row">
                  <div class="flex flex-col leading-tight">
                    <span class="text-xs font-semibold text-slate-800">{{
                        row.totalNodes ?? '—'
                      }}</span>
                    <span class="mt-0.5 text-[0.75rem] text-slate-400">{{ row.completedNodes ?? '—' }} done</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="progress">
                <th mat-header-cell *matHeaderCellDef>PROGRESS</th>
                <td mat-cell *matCellDef="let row">
                  <div class="flex min-w-[120px] items-center gap-3">
                    <div class="progress-track">
                      <span
                        [class]="'progress-fill progress-fill--' + row.status.toLowerCase()"
                        [style.width.%]="row.progressPercent ?? 0"
                      ></span>
                    </div>
                    <span class="text-xs font-medium text-slate-600"
                    >{{ row.progressPercent ?? '—' }}%</span>
                  </div>
                </td>
              </ng-container>

              <ng-container matColumnDef="lastUpdated">
                <th mat-header-cell *matHeaderCellDef>LAST UPDATED</th>
                <td mat-cell *matCellDef="let row" class="font-normal text-slate-500">{{ row.lastUpdatedLabel }}</td>
              </ng-container>

              <ng-container matColumnDef="action">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let row" class="action-cell">
                  <button
                    mat-flat-button
                    color="primary"
                    type="button"
                    [attr.data-cy]="'network-migration-open-' + row.id"
                    class="row-action-btn"
                    (click)="$event.stopPropagation(); openMigrationWorkflow(row)">
                    <span>{{ row.actionLabel ?? 'Open' }}</span>
                    <span aria-hidden="true">→</span>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns"
                class="migration-table-row--interactive"
                [class.migration-table-row--highlighted]="row.isHighlighted"
                [attr.data-cy]="'network-migration-row-' + row.id"
                tabindex="0"
                (click)="openMigrationWorkflow(row)"
                (keydown.enter)="openMigrationWorkflow(row)"
                (keydown.space)="$event.preventDefault(); openMigrationWorkflow(row)">
              </tr>
            </table>
          </div>
        } @else {
          <div
            class="px-6 py-10 text-xs font-medium text-slate-500"
            data-cy="network-migration-empty-state">
            No buildings match the current filters.
          </div>
        }

        <div class="border-t border-slate-100 px-4 py-3 text-[0.75rem] text-slate-400">
          {{ resultsLabel() }}
        </div>
      </mat-card>
    }
  </section>
</main>
